import { getRefreshRequest, getRequest, oauth2Config, RefreshRequest, Request } from '../data';
import { JWTAuthToken, SessionState, TokenResponse, TokenState } from '../utils/AuthTypes';
import { jwtDecode } from 'jwt-decode';
import queryString from 'query-string';

export const generateOAuth2State = (): string => generateRandomString(32);
export const generateOAuth2Nonce = (): string => generateRandomString(32);

const generateRandomString = (length: number = 32): string => {
  const mapDecToHex = (dec: number): string => ('0' + dec.toString(16)).slice(-2);
  const arr: Uint8Array = new Uint8Array(length / 2);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, mapDecToHex).join('');
};

export const getTokenWithCodeOAuth2 = async (authCode: string): Promise<{ auth: string; refresh: string }> => {
  const request: Request = getRequest(authCode);
  return requestTokens(request);
};

export const getRefresh = async (refreshToken: string): Promise<{ auth: string; refresh: string }> => {
  const request: RefreshRequest = getRefreshRequest(refreshToken);
  return requestTokens(request);
};

const requestTokens = async (request: Request | RefreshRequest): Promise<{ auth: string; refresh: string }> => {
  const response: Response = await fetch(oauth2Config.tokenUrl, {
    method: 'POST',
    cache: 'no-cache',
    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
    body: queryString.stringify(request),
  });

  if (!response.ok) {
    throw new Error(`Request error (${response.status}): ${response.statusText}`);
  }

  const data: TokenResponse = await response.json();

  if (data.access_token === undefined || data.token_type?.toLowerCase() !== 'bearer') {
    throw new Error('Malformed token response');
  }

  const auth: string = String(data.access_token);
  const refresh: string = data.refresh_token !== undefined ? String(data.refresh_token) : undefined;

  return {auth, refresh};
};

export const createTokenState = (authToken: string, refreshToken: string): TokenState => {
  const payload: JWTAuthToken = jwtDecode<JWTAuthToken>(authToken);
  const authTokenExpires: number = Number(payload.exp) * 1000;
  const now: number = new Date().getTime();

  if (authTokenExpires < now) {
    return {
      auth: null,
      refresh: {token: refreshToken, expires: null},
    };
  } else {
    return {
      auth: {token: authToken, expires: authTokenExpires},
      refresh: {token: refreshToken, expires: null},
    };
  }
};

export const createSessionState = (auth: string): SessionState => {

  const decoded: JWTAuthToken = jwtDecode<JWTAuthToken>(auth);

  if (!decoded.hasOwnProperty('client_id') || decoded.client_id !== oauth2Config.clientId) {
    throw new Error('Invalid or missing clientId in token');
  }

  if (!decoded.hasOwnProperty('exp')) {
    throw new Error('No expiration date in token');
  }

  const user_id: number = decoded.hasOwnProperty('user_id') ? Number(decoded.user_id) : null;
  const user_name: string = decoded.hasOwnProperty('user_name') ? String(decoded.user_name) : '<unknown>';
  const user_givenname: string = decoded.hasOwnProperty('user_givenname') ? String(decoded.user_givenname) : '<unknown>';
  const user_surname: string = decoded.hasOwnProperty('user_surname') ? String(decoded.user_surname) : '<unknown>';
  const roles: string[] = decoded.hasOwnProperty('authorities') ? decoded.authorities : [];
  const user_displayname: string = user_givenname && user_surname ? `${user_givenname} ${user_surname}` : user_name;

  return {
    user_id,
    user_name,
    user_givenname,
    user_surname,
    user_displayname,
    roles,
  };
};

export const logoutOnAuthServer = async (): Promise<void> => {
  const url: string = oauth2Config.logoutUrl;
  if (url == null) {
    return;
  }

  await fetch(url, {
    credentials: 'include',
    redirect: 'follow',
  });
};
